import { useState } from 'react';
import Textfield from '@atlaskit/textfield';
import { ButtonType } from 'core/enums/button-type';
import { useDialog } from 'core/providers/DialogProvider';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import { ISharedDialogButton } from 'shared/components/dialog-base/dialog-base-interface';
import DeliveryRequestRoutingApiService from 'core/api/delivery-request-routing/delivery-request-routing-api.service';
import { IPartner, IRoutingTable } from 'core/api/delivery-request-routing/delivery-request-routing-api-interface';

/**
 * Adding or editing one external partner.
 *
 * Three fields, because a partner is three things: who they are, where a
 * forwarded request is emailed, and what the requester rings to change or
 * cancel it. Entered once here and then chosen by name against a request type,
 * so a changed number does not have to be found in seven places.
 */

interface IProps {
  /** Absent when adding. */
  partner?: IPartner;
  onSaved: (routing: IRoutingTable) => void;
}

const AddEditPartnerDialog = ({ partner, onSaved }: IProps) => {
  const dialog = useDialog();

  const [name, setName] = useState(partner?.name ?? '');
  const [email, setEmail] = useState(partner?.email ?? '');
  const [phone, setPhone] = useState(partner?.phone ?? '');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const save = async () => {
    setSaving(true);
    setError(undefined);

    try {
      const response = await DeliveryRequestRoutingApiService.savePartner({
        ...(partner ? { uid: partner.uid } : {}),
        name: name.trim(),
        email: email.trim(),
        // Omitted rather than sent empty, so a partner who never had a number
        // reads the same as one whose number was cleared.
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      });
      onSaved(response.routing);
      dialog?.closeDialog();
    } catch (caught) {
      // The dialog stays open: a refused save that closed would look like one
      // that worked.
      setError((caught as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!partner) return;

    setRemoving(true);
    setError(undefined);

    try {
      const response = await DeliveryRequestRoutingApiService.removePartner(partner.uid);
      onSaved(response.routing);
      dialog?.closeDialog();
    } catch (caught) {
      // The backend refuses to remove a partner a request type still points at,
      // and names the types. That message is the whole point, so it is shown as
      // it came rather than replaced with something generic.
      setError((caught as Error).message);
    } finally {
      setRemoving(false);
    }
  };

  const buttons: ISharedDialogButton[] = [
    ...(partner
      ? [
          {
            key: 'remove',
            type: ButtonType.Button,
            buttonProps: {
              onClick: remove,
              appearance: 'subtle' as const,
              label: 'Remove',
              type: 'button' as const,
              isLoading: removing,
              testId: 'remove-partner',
            },
          },
        ]
      : []),
    {
      key: 'cancel',
      type: ButtonType.Button,
      buttonProps: { onClick: () => dialog?.closeDialog(), appearance: 'subtle', label: 'Cancel', type: 'button' },
    },
    {
      key: 'save',
      type: ButtonType.Button,
      buttonProps: {
        onClick: save,
        appearance: 'primary',
        label: 'Save partner',
        type: 'button',
        isLoading: saving,
        testId: 'save-partner',
      },
    },
  ];

  return (
    <SharedDialogBase
      title={partner ? `Edit ${partner.name}` : 'Add a partner'}
      customButtons={buttons}
      customContentTemplate={
        <div className='max-h-[65vh] w-[32rem] max-w-full overflow-y-auto p-4'>
        {error && (
          <div data-testid='partner-form-error' className='mb-4 rounded border border-red-300 bg-red-50 p-3'>
            <p className='body-01 text-red-700'>{error}</p>
          </div>
        )}

        <p className='body-02 font-semibold'>Name</p>
        <p className='body-01 mt-1 text-gray-500'>
          How they read in the dropdown, on the routing table, and in reporting.
        </p>
        <div className='mt-1'>
          <Textfield
            value={name}
            onChange={(event) => setName((event.target as HTMLInputElement).value)}
            placeholder='Western'
            testId='partner-name'
          />
        </div>

        <p className='body-02 mt-4 font-semibold'>Where requests are emailed</p>
        <div className='mt-1'>
          <Textfield
            value={email}
            onChange={(event) => setEmail((event.target as HTMLInputElement).value)}
            placeholder='bookings@example.com'
            testId='partner-email'
          />
        </div>

        <p className='body-02 mt-4 font-semibold'>Phone number</p>
        <p className='body-01 mt-1 text-gray-500'>
          What the requester rings to change or cancel. Quoted back to them in the email confirming their booking, so
          leave it blank only if there is nothing for them to ring.
        </p>
        <div className='mt-1'>
          <Textfield
            value={phone}
            onChange={(event) => setPhone((event.target as HTMLInputElement).value)}
            placeholder='01234 567890'
            testId='partner-phone'
          />
        </div>
        </div>
      }
    />
  );
};

export default AddEditPartnerDialog;
